// Этап 17.3: «Кабинет франшизы» в Mini App. Заменяет заглушку BossPlaceholderPage.
import NotificationBell from '../components/NotificationBell';
// Доступен boss (вся франшиза) и network_manager (своя сеть).
// Этап 19.5: network_manager может оформить заказ — тап по активной точке открывает
// каталог для неё. boss остаётся read-only (только обзор). Добавлен поиск по адресу.
import { useEffect, useMemo, useState } from 'react';
import { getDepositMe, getFranchiseMe } from '../api';
import type { FranchiseMe, CoffeeShop, DepositAccount } from '../types';

const SHOP_STATUS: Record<string, { label: string; color: string }> = {
  active: { label: 'Активна', color: '#34c759' },
  inactive: { label: 'Неактивна', color: 'var(--tg-hint)' },
  pending: { label: 'Ожидание', color: '#ff9500' },
  blocked: { label: 'Заблокирована', color: '#ff3b30' },
};

const DELIVERY_LABEL: Record<string, string> = {
  city: 'Город',
  intercity: 'Межгород',
};

const fmtPrice = (n: number | string) =>
  Number(n).toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 2 });

function ShopCard({
  shop,
  orderable,
  onOrder,
  deposit,
}: {
  shop: CoffeeShop;
  // Этап 19.5: можно ли с этой карточки оформить заказ (NM + активная точка).
  orderable: boolean;
  onOrder?: (shop: CoffeeShop) => void;
  // Блок D: депозит точки (остаток), если загружен.
  deposit?: DepositAccount | null;
}) {
  const st = SHOP_STATUS[shop.status] ?? { label: shop.status, color: 'var(--tg-hint)' };
  const dimmed = shop.status === 'inactive' || shop.status === 'blocked';
  return (
    <div
      className="card"
      style={{
        opacity: dimmed ? 0.55 : 1,
        cursor: orderable ? 'pointer' : 'default',
      }}
      role={orderable ? 'button' : undefined}
      onClick={orderable ? () => onOrder?.(shop) : undefined}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>{shop.name}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: st.color, whiteSpace: 'nowrap' }}>
          ● {st.label}
        </span>
      </div>
      <div className="hint" style={{ fontSize: 13, lineHeight: 1.4 }}>{shop.address}</div>
      {deposit && (
        <div style={{ marginTop: 4, fontSize: 13, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <span className="hint">💳 Депозит, доступно</span>
          <span style={{ fontWeight: 700, color: deposit.can_order ? 'var(--tg-button)' : '#ff3b30' }}>
            {fmtPrice(deposit.available)}
          </span>
        </div>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 4,
        }}
      >
        <span className="hint" style={{ fontSize: 12 }}>
          Доставка: {DELIVERY_LABEL[shop.delivery_type] ?? shop.delivery_type}
        </span>
        {orderable && (
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tg-button)', whiteSpace: 'nowrap' }}>
            Заказать ›
          </span>
        )}
      </div>
    </div>
  );
}

export default function FranchiseCabinetPage({
  // Этап 19.5: коллбэк «оформить заказ для точки». Передаётся из App только для NM.
  onOrderForShop,
  // Блок D: открыть страницу «Депозит» (остатки, пополнение, счета).
  onDeposits,
  // Блок «Уведомления в приложении»: колокольчик в шапке кабинета.
  onNotifications,
}: {
  onOrderForShop?: (shop: CoffeeShop) => void;
  onDeposits?: () => void;
  onNotifications?: () => void;
}) {
  const [data, setData] = useState<FranchiseMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Этап 19.5: строка поиска по названию/адресу точки.
  const [query, setQuery] = useState('');
  // Блок D: депозиты по точкам (остаток на карточке и сводка сверху).
  const [deposits, setDeposits] = useState<DepositAccount[]>([]);
  useEffect(() => {
    getDepositMe()
      .then(({ data }) => setDeposits(data))
      .catch(() => { /* остатки не критичны для кабинета */ });
  }, []);
  const depositByShop = useMemo(() => {
    const map: Record<string, DepositAccount> = {};
    for (const d of deposits) map[d.coffee_shop_id] = d;
    return map;
  }, [deposits]);
  const totalAvailable = deposits.reduce((s, d) => s + Number(d.available), 0);
  const lowCount = deposits.filter(d => !d.can_order).length;

  useEffect(() => {
    getFranchiseMe()
      .then(({ data }) => setData(data))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Этап 19.5: заказывать может только NM (boss — read-only). Фильтр по подстроке
  // в названии или адресе (без учёта регистра) — удобно при большой сети.
  const canOrder = !!data?.is_network_manager && !!onOrderForShop;
  const filteredShops = useMemo(() => {
    const all = data?.coffee_shops ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (s) => s.name.toLowerCase().includes(q) || s.address.toLowerCase().includes(q),
    );
  }, [data, query]);

  if (loading) {
    return (
      <div className="container" style={{ padding: '32px 16px' }}>
        <p className="hint">Загрузка кабинета…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="container" style={{ padding: '32px 16px' }}>
        <p className="hint" style={{ color: '#ff3b30' }}>
          Не удалось загрузить кабинет: {error ?? 'нет данных'}
        </p>
      </div>
    );
  }

  const roleLabel = data.is_boss ? 'Владелец франшизы' : 'Управляющий сетью';
  const roleIcon = data.is_boss ? '👑' : '🗂️';
  const shopsTitle = data.is_boss ? 'Кофейни франшизы' : 'Кофейни вашей сети';
  const emptyText = data.is_boss
    ? 'Пока нет действующих кофеен франшизы.'
    : 'За вами пока не закреплены кофейни. Их добавляет администратор или владелец франшизы.';
  // Поиск показываем, когда точек достаточно много, чтобы он был полезен.
  const showSearch = data.coffee_shops.length > 5;

  return (
    <div className="container" style={{ padding: '16px 16px 32px' }}>
      {/* Шапка кабинета */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '16px',
          borderRadius: 12,
          background: 'var(--tg-secondary-bg)',
          marginBottom: 20,
        }}
      >
        <div style={{ fontSize: 32 }}>{roleIcon}</div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{data.user.full_name ?? 'Кабинет франшизы'}</div>
          <div className="hint" style={{ fontSize: 13 }}>{roleLabel}</div>
        </div>
        {onNotifications && (
          <div style={{ marginLeft: 'auto' }}><NotificationBell onOpen={onNotifications} /></div>
        )}
      </div>

      {/* Блок D: депозиты — сводка и переход на страницу */}
      {onDeposits && (
        <div className="card" style={{ marginBottom: 16, cursor: 'pointer' }} role="button" onClick={onDeposits}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>💰 Депозиты</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tg-button)', whiteSpace: 'nowrap' }}>Открыть ›</span>
          </div>
          <div className="hint" style={{ marginTop: 4, fontSize: 13 }}>
            {deposits.length > 0
              ? `Доступно всего ${fmtPrice(totalAvailable)}${lowCount > 0 ? ` · не могут заказывать: ${lowCount}` : ''}`
              : 'Остатки по точкам, пополнение, счета'}
          </div>
        </div>
      )}

      {/* Кофейни */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>{shopsTitle}</h3>
        <span className="hint" style={{ fontSize: 13 }}>{data.coffee_shops.length}</span>
      </div>

      {/* Этап 19.5: подсказка для NM, что точку можно открыть для заказа */}
      {canOrder && data.coffee_shops.length > 0 && (
        <p className="hint" style={{ fontSize: 12, margin: '0 0 12px' }}>
          Нажмите на активную точку, чтобы оформить для неё заказ.
        </p>
      )}

      {/* Этап 19.5: поиск по названию/адресу */}
      {showSearch && (
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Поиск по адресу или названию…"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '10px 12px',
            marginBottom: 12,
            borderRadius: 10,
            border: '1px solid var(--tg-secondary-bg)',
            background: 'var(--tg-secondary-bg)',
            color: 'var(--tg-text)',
            fontSize: 14,
            fontFamily: 'inherit',
            outline: 'none',
          }}
        />
      )}

      {data.coffee_shops.length === 0 ? (
        <p className="hint">{emptyText}</p>
      ) : filteredShops.length === 0 ? (
        <p className="hint">По запросу «{query}» точек не найдено.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredShops.map((s) => (
            <ShopCard
              key={s.id}
              shop={s}
              orderable={canOrder && s.status === 'active'}
              onOrder={onOrderForShop}
              deposit={depositByShop[s.id] ?? null}
            />
          ))}
        </div>
      )}

      {/* Заглушка-подсказка о будущих функциях */}
      <p className="hint" style={{ fontSize: 12, marginTop: 24, lineHeight: 1.5, textAlign: 'center' }}>
        Управление сотрудниками и просмотр заказов появятся в следующих обновлениях кабинета.
      </p>
    </div>
  );
}
